/**
 * YETSAN Messenger - Firma İçi Anlık Mesajlaşma
 * Sağ altta floating chat penceresi
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  MessageCircle, X, Send, Paperclip, Download, Check, CheckCheck,
  Circle, Search, ArrowLeft, ChevronDown, Minimize2, Maximize2,
  Users, File, Image, FileText, Eye
} from 'lucide-react';
import { gorunurAralik } from '../utils/gorunurAralik';

const API = process.env.REACT_APP_BACKEND_URL + '/api/panel/messenger';
const MESSENGER_SOUND = '/sounds/notification.wav';  // messenger.mp3 yoktu (404); mesaj bildirim sesi

// Asıl messenger içeriği - sadece yetkili kullanıcılar için
const MessengerContent = ({ user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [recentChats, setRecentChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadBySender, setUnreadBySender] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const heartbeatRef = useRef(null);
  const audioRef = useRef(null);
  const prevUnreadRef = useRef(0);

  const userEmail = user?.email;
  const userName = user?.name;
  const userRole = user?.role;

  // Mesaj sesi çal
  const playMessageSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(MESSENGER_SOUND);
        audioRef.current.volume = 0.6;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch (err) {}
  }, []);

  // Heartbeat - online durumu güncelle
  const sendHeartbeat = useCallback(async () => {
    if (!userEmail) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/heartbeat`, {
        user_id: user?.id,
        email: userEmail,
        name: userName,
        role: userRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {}
  }, [userEmail, userName, userRole, user?.id]);

  // Online kullanıcıları getir
  const fetchOnlineUsers = useCallback(async () => {
    if (!userEmail) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/online-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setOnlineUsers(res.data.users?.filter(u => u.email !== userEmail) || []);
      }
    } catch (err) {}
  }, [userEmail]);

  // Son konuşmaları getir
  const fetchRecentChats = useCallback(async () => {
    if (!userEmail) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/recent-chats/${userEmail}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setRecentChats(res.data.chats || []);
      }
    } catch (err) {}
  }, [userEmail]);

  // Okunmamış sayısı
  const fetchUnreadCount = useCallback(async () => {
    if (!userEmail) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/unread-count/${userEmail}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const newCount = res.data.total || 0;
        // Yeni mesaj geldiyse ses çal
        if (newCount > prevUnreadRef.current) {
          playMessageSound();
        }
        prevUnreadRef.current = newCount;
        setUnreadCount(newCount);
        setUnreadBySender(res.data.by_sender || {});
      }
    } catch (err) {}
  }, [userEmail, playMessageSound]);

  // Mesajları getir
  const fetchMessages = useCallback(async () => {
    if (!activeChat || !userEmail) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/conversation/${userEmail}/${activeChat.email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setMessages(res.data.messages || []);
        // Okunmamış sayısını güncelle
        fetchUnreadCount();
      }
    } catch (err) {}
  }, [activeChat, userEmail, fetchUnreadCount]);

  // İlk yükleme
  useEffect(() => {
    sendHeartbeat();
    fetchOnlineUsers();
    fetchRecentChats();
    fetchUnreadCount();

    // Heartbeat her 60 saniyede (30'dan 60'a çıkarıldı)
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);
    
    // Polling her 20 saniyede (5'ten 20'ye çıkarıldı - performans iyileştirmesi)
    // Yalnız görünür sekmede (arka plan sekmeleri sunucuyu doyuruyordu, 2026-10-20)
    const yoklamaDurdur = gorunurAralik(() => {
      fetchOnlineUsers();
      fetchUnreadCount();
      if (isOpen) fetchRecentChats();
    }, 30000);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      yoklamaDurdur();
    };
  }, [sendHeartbeat, fetchOnlineUsers, fetchRecentChats, fetchUnreadCount, isOpen]);

  // Aktif chat değişince mesajları getir
  useEffect(() => {
    if (activeChat) {
      fetchMessages();
      // Mesaj polling her 8 saniyede (3'ten 8'e çıkarıldı)
      return gorunurAralik(fetchMessages, 8000);
    }
  }, [activeChat, fetchMessages]);

  // Mesaj scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mesaj gönder
  const handleSend = async () => {
    if ((!newMessage.trim() && !selectedFile) || !activeChat || sending) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      let fileData = null;
      let fileName = null;
      let fileType = null;
      
      if (selectedFile) {
        const reader = new FileReader();
        fileData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(selectedFile);
        });
        fileName = selectedFile.name;
        fileType = selectedFile.type;
      }

      const res = await axios.post(`${API}/send`, {
        sender_email: userEmail,
        sender_name: userName,
        receiver_email: activeChat.email,
        message: newMessage.trim(),
        file: fileData,
        file_name: fileName,
        file_type: fileType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setNewMessage('');
        setSelectedFile(null);
        fetchMessages();
        fetchRecentChats();
      } else {
        console.error('Mesaj gönderilemedi:', res.data.error);
      }
    } catch (err) {
      console.error('Mesaj gönderilemedi:', err);
    } finally {
      setSending(false);
    }
  };

  // Dosya indir
  const handleDownload = async (msgId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/download/${msgId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && res.data.file) {
        const link = document.createElement('a');
        link.href = res.data.file;
        link.download = fileName || 'dosya';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Dosya indirilemedi:', err);
    }
  };

  // Dosya ikonu
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <Image size={12} className="text-purple-500" />;
    if (type?.includes('pdf')) return <FileText size={12} className="text-red-500" />;
    return <File size={12} className="text-blue-500" />;
  };

  // Zaman formatı - Detaylı saat bilgisi
  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diff = (now - d) / 1000 / 60; // dakika
    if (diff < 1) return 'Az önce';
    if (diff < 60) return `${Math.floor(diff)}dk`;
    if (diff < 1440) return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  };

  // Tam zaman formatı - Okunma bilgisi için
  const formatFullTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  // Filtrelenmiş kullanıcılar
  const filteredUsers = onlineUsers.filter(u => 
    !searchQuery || 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Messenger aç - minimized veya kapalıysa
  const handleOpenMessenger = () => {
    setIsOpen(true);
    setIsMinimized(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleOpenMessenger}
        className={`fixed bottom-3 right-3 z-50 w-11 h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg flex items-center justify-center transition-all ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="messenger-toggle-btn"
      >
        <MessageCircle size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-3 right-3 z-50 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden transition-all ${
            isMinimized ? 'w-56 h-11 cursor-pointer' : 'w-80 h-[440px]'
          }`}
          onClick={isMinimized ? handleOpenMessenger : undefined}
          data-testid="messenger-window"
        >
          {/* Header */}
          <div 
            className={`bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-3 py-2 flex items-center justify-between ${isMinimized ? 'cursor-pointer' : ''}`}
            onClick={isMinimized ? handleOpenMessenger : (activeChat ? () => setActiveChat(null) : undefined)}
          >
            <div className="flex items-center gap-2">
              {activeChat && !isMinimized && (
                <button onClick={(e) => { e.stopPropagation(); setActiveChat(null); }} className="hover:bg-white/20 p-0.5 rounded">
                  <ArrowLeft size={14} />
                </button>
              )}
              <div>
                <h3 className="text-xs font-bold">
                  {activeChat ? activeChat.name : 'YETSAN Messenger'}
                </h3>
                {activeChat && !isMinimized && (
                  <span className={`text-[10px] ${activeChat.online ? 'text-green-300' : 'text-indigo-200'}`}>
                    {activeChat.online ? '● Çevrimiçi' : 'Çevrimdışı'}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-0.5">
              {/* Okunmamış mesaj sayısı - minimized header'da */}
              {isMinimized && unreadCount > 0 && (
                <span className="mr-1 min-w-[16px] h-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <button 
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }} 
                className="hover:bg-white/20 p-1 rounded"
              >
                {isMinimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} 
                className="hover:bg-white/20 p-1 rounded"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Chat List View */}
              {!activeChat && (
                <div className="flex flex-col h-[calc(100%-36px)]">
                  {/* Search */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Kullanıcı ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-7 pr-2 py-1.5 bg-slate-100 rounded-lg text-[11px] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        data-testid="messenger-search-input"
                      />
                    </div>
                  </div>

                  {/* Recent Chats */}
                  {recentChats.length > 0 && !searchQuery && (
                    <div className="border-b">
                      <div className="px-2 py-1.5 bg-slate-50">
                        <span className="text-[10px] font-semibold text-slate-500">Son Konuşmalar</span>
                      </div>
                      <div className="max-h-32 overflow-y-auto">
                        {recentChats.slice(0, 5).map((chat) => (
                          <button
                            key={chat.email}
                            onClick={() => setActiveChat(chat)}
                            className="w-full flex items-center gap-2 px-2 py-2 hover:bg-slate-50 transition text-left"
                            data-testid={`recent-chat-${chat.email}`}
                          >
                            <div className="relative">
                              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                                {chat.name?.charAt(0) || '?'}
                              </div>
                              {chat.online && (
                                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] font-semibold text-slate-800 truncate">{chat.name}</span>
                                <span className="text-[9px] text-slate-400">{formatTime(chat.last_time)}</span>
                              </div>
                              <p className="text-[10px] text-slate-500 truncate">
                                {chat.last_sender === userEmail && <span className="text-slate-400">Sen: </span>}
                                {chat.last_message}
                              </p>
                            </div>
                            {chat.unread > 0 && (
                              <span className="min-w-[16px] h-4 px-1 bg-indigo-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {chat.unread}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Online Users */}
                  <div className="flex-1 overflow-y-auto">
                    <div className="px-2 py-1.5 bg-slate-50 sticky top-0">
                      <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                        <Users size={10} /> Tüm Ekip ({filteredUsers.length})
                      </span>
                    </div>
                    {filteredUsers.length === 0 ? (
                      <div className="p-4 text-center text-[10px] text-slate-400">
                        Kullanıcı bulunamadı
                      </div>
                    ) : (
                      filteredUsers.map((u) => (
                        <button
                          key={u.email}
                          onClick={() => setActiveChat(u)}
                          className="w-full flex items-center gap-2 px-2 py-2 hover:bg-slate-50 transition text-left"
                          data-testid={`user-${u.email}`}
                        >
                          <div className="relative">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold ${
                              u.online ? 'bg-gradient-to-br from-green-500 to-emerald-600' : 'bg-slate-400'
                            }`}>
                              {u.name?.charAt(0) || '?'}
                            </div>
                            {u.online && (
                              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-[11px] font-medium text-slate-800 truncate block">{u.name}</span>
                            <span className="text-[9px] text-slate-400">{u.role}</span>
                          </div>
                          {unreadBySender[u.email] > 0 && (
                            <span className="min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                              {unreadBySender[u.email]}
                            </span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Chat View */}
              {activeChat && (
                <div className="flex flex-col h-[calc(100%-36px)]">
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-slate-50">
                    {messages.length === 0 ? (
                      <div className="text-center py-4 text-[10px] text-slate-400">
                        Henüz mesaj yok. Bir şeyler yaz!
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isMine = msg.sender_email === userEmail;
                        const isImage = msg.file_type?.startsWith('image/');
                        return (
                          <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] rounded-xl px-2 py-1 ${
                              isMine 
                                ? 'bg-indigo-600 text-white rounded-br-sm' 
                                : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                            }`}>
                              {msg.message && (
                                <p className="text-[10px] whitespace-pre-wrap break-words leading-relaxed">{msg.message}</p>
                              )}
                              {msg.has_file && (
                                <>
                                  {/* Resim önizlemesi - hem gönderen hem alıcı görebilir */}
                                  {isImage && msg.file_preview ? (
                                    <div className="mt-1 mb-0.5">
                                      <img 
                                        src={msg.file_preview} 
                                        alt={msg.file_name}
                                        className="max-w-full max-h-28 rounded-lg cursor-pointer hover:opacity-90"
                                        onClick={() => handleDownload(msg.id, msg.file_name)}
                                      />
                                      <p className={`text-[8px] mt-0.5 ${isMine ? 'text-indigo-200' : 'text-slate-400'}`}>
                                        {msg.file_name}
                                      </p>
                                    </div>
                                  ) : (
                                    <button 
                                      onClick={() => handleDownload(msg.id, msg.file_name)}
                                      className={`flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded-lg text-[9px] ${
                                        isMine ? 'bg-indigo-500 hover:bg-indigo-400' : 'bg-slate-100 hover:bg-slate-200'
                                      }`}
                                    >
                                      {getFileIcon(msg.file_type)}
                                      <span className="truncate max-w-[80px]">{msg.file_name}</span>
                                      <Download size={9} />
                                    </button>
                                  )}
                                </>
                              )}
                              {/* Mesaj bilgisi: Saat + Görüldü durumu */}
                              <div className={`flex items-center justify-end gap-0.5 mt-0.5 ${isMine ? 'text-indigo-200' : 'text-slate-400'}`}>
                                <span className="text-[8px]">{formatTime(msg.created_at)}</span>
                                {isMine && (
                                  msg.read ? (
                                    <span className="flex items-center gap-0.5" title={msg.read_at ? `Görüldü: ${formatFullTime(msg.read_at)}` : 'Görüldü'}>
                                      <CheckCheck size={9} className="text-blue-300" />
                                      {msg.read_at && (
                                        <span className="text-[7px] text-blue-300">{formatFullTime(msg.read_at)}</span>
                                      )}
                                    </span>
                                  ) : (
                                    <Check size={9} />
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Selected File Preview */}
                  {selectedFile && (
                    <div className="px-2 py-1 bg-indigo-50 border-t">
                      <div className="flex items-center justify-between gap-1">
                        {selectedFile.type?.startsWith('image/') ? (
                          <div className="flex items-center gap-1.5">
                            <img 
                              src={URL.createObjectURL(selectedFile)} 
                              alt="Önizleme" 
                              className="w-8 h-8 object-cover rounded"
                            />
                            <span className="text-[9px] text-indigo-700 truncate max-w-[140px]">{selectedFile.name}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-[9px] text-indigo-700">
                            {getFileIcon(selectedFile.type)}
                            <span className="truncate max-w-[140px]">{selectedFile.name}</span>
                          </div>
                        )}
                        <button onClick={() => setSelectedFile(null)} className="text-red-500 hover:text-red-700 p-0.5">
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Input */}
                  <div className="p-1.5 border-t bg-white">
                    <div className="flex items-center gap-1">
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={(e) => setSelectedFile(e.target.files?.[0])}
                      />
                      <button 
                        onClick={() => fileInputRef.current?.click()}
                        className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 rounded-lg transition"
                        data-testid="messenger-attach-btn"
                      >
                        <Paperclip size={14} />
                      </button>
                      <input
                        type="text"
                        placeholder="Mesaj yaz..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                        className="flex-1 px-2 py-1.5 bg-slate-100 rounded-full text-[10px] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        data-testid="messenger-input"
                      />
                      <button
                        onClick={handleSend}
                        disabled={sending || (!newMessage.trim() && !selectedFile)}
                        className="p-1.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                        data-testid="messenger-send-btn"
                      >
                        <Send size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

// Ana wrapper component
const YetsanMessengerWrapper = () => {
  const { user } = useAuth();
  
  // 🚫 MÜŞTERİLER İÇİN ERKEN ÇIKIŞ - HİÇBİR HOOK ÇALIŞMAZ
  const userRole = user?.role;
  const isMusteri = userRole === 'musteri' || userRole === 'client';
  
  // Müşteriler için hiçbir şey render etme ve hook'ları çalıştırma
  if (isMusteri) {
    return null;
  }
  
  return <MessengerContent user={user} />;
};

export default YetsanMessengerWrapper;
